'use client';

import { Fragment } from 'react';
import type {
  AncestorFolder,
  Project,
} from '@frameio/graphql-client/generated';
import { VisuallyHidden } from '@frameio/vapor';
import {
  AssetPathnameContainer,
  Pathnames,
  PathnameSegmentText,
  PathnameSegmentTextMid,
} from './AssetPathnameSegment';
import TEXT from './generated/TEXT/AssetPathnameResult';
import type { PathnameSegment, PathnameWithFoldersProps } from './types';

TEXT.DEFINE({
  /** Fallback text for folders without a name */
  untitled: 'untitled folder',
});

const TEST_IDS_CONTAINER = 'asset-pathname-result';
const TEST_IDS_ELLIPSIS = 'asset-pathname-ellipsis';
const PATHNAME_SEPARATOR = '/';

/**
 * Displays a breadcrumb pathname for assets in search results.
 *
 * This component intelligently truncates long folder hierarchies to maintain
 * readability while preserving the most important context (project and immediate parent).
 *
 * ## Behavior Rules:
 *
 * 1. **No ancestors or root only**: Shows just the project name
 *    - Example: "Demo Project"
 *
 * 2. **One ancestor folder**: Shows project → folder
 *    - Example: "Demo Project / Folder 1"
 *
 * 3. **Multiple ancestor folders**: Shows project → folder path (will ellipsize) → last folder
 *    - Example: "Demo Project / First Folder / Second fold... / Final Folder"
 *
 * ## Design Considerations:
 * - Uses ellipsis (...) to indicate truncated middle segments
 * - Always shows the project name and last folder for maximum context
 * - Shows the first folder when multiple folders exist to provide initial context
 * - Maintains consistent visual hierarchy with tertiary text color
 * - Provides proper accessibility labels for screen readers
 */
export const AssetPathnameResult = ({
  ancestorFolders,
  project,
}: PathnameWithFoldersProps) => {
  // Create pathname segments based on the folder hierarchy depth
  const pathnameSegments = createPathnameSegments(project, ancestorFolders);
  const pathnameLabel = pathnameSegments.map(({ name }) => name).join('');
  return (
    <AssetPathnameContainer
      aria-label={pathnameLabel}
      data-testid={TEST_IDS_CONTAINER}
    >
      <Pathnames>
        {pathnameSegments.map(({ id, isEllipsis, name }) => (
          <Fragment key={id}>
            {isEllipsis ? (
              <PathnameSegmentTextMid
                aria-hidden={isEllipsis}
                data-testid={isEllipsis ? TEST_IDS_ELLIPSIS : undefined}
              >
                {name}
              </PathnameSegmentTextMid>
            ) : (
              <PathnameSegmentText key={id} segments={pathnameSegments.length}>
                {name}
              </PathnameSegmentText>
            )}
          </Fragment>
        ))}
      </Pathnames>
      <AssetPathnameScreenReader
        ancestorFolders={ancestorFolders}
        project={project}
      />
    </AssetPathnameContainer>
  );
};

/**
 * Creates accessible breadcrumb text for screen readers.
 * Provides the full folder hierarchy for better context.
 */
function AssetPathnameScreenReader({
  ancestorFolders,
  project,
}: PathnameWithFoldersProps) {
  const segments = [project.name];

  if (ancestorFolders?.length) {
    // Skip the root folder and add all other folders
    const folderNames = ancestorFolders
      .slice(1) // Skip root folder
      .map(({ name }) => name || TEXT.untitled);
    segments.push(...folderNames);
  }

  const screenReaderContent = segments.join(` ${PATHNAME_SEPARATOR} `);

  return (
    <VisuallyHidden aria-live="polite">{screenReaderContent}</VisuallyHidden>
  );
}

/**
 * Creates pathname segments based on the folder hierarchy depth.
 * Shows project + first folder + ellipsis + last folder for maximum context.
 *
 * @param project - Project containing the asset
 * @param ancestorFolders - Array of ancestor folders (first is root, last is immediate parent)
 * @returns Array of pathname segments to display
 */
function createPathnameSegments(
  project: Pick<Project, 'id' | 'name'>,
  ancestorFolders?: AncestorFolder[]
): PathnameSegment[] {
  // Always start with the project
  const segments = [
    {
      id: project.id,
      name: project.name,
    },
  ];

  // If no additional folders, just return project
  if (!ancestorFolders?.length || ancestorFolders.length === 1) {
    return segments;
  }

  // Get all non-root folders (skip index 0)
  const nonRootFolders = ancestorFolders.slice(1);

  // If only one folder, show project + folder and exit
  if (nonRootFolders.length === 1) {
    segments.push({
      id: nonRootFolders[0].id,
      name: `/ ${nonRootFolders[0].name || TEXT.untitled}`,
    });
    return segments;
  }

  // For multiple folders, show: project + ellipsis folder group + last folder
  const lastFolder = nonRootFolders.pop();

  segments.push(
    // add middle folders as one segment
    {
      id: TEST_IDS_ELLIPSIS,
      isEllipsis: true,
      name: ` ${nonRootFolders
        .map(({ name }) => {
          return `/ ${name || TEXT.untitled}`;
        })
        .join(' ')}`,
    } as PathnameSegment,
    // add last folder
    {
      id: lastFolder?.id as string,
      name: `/ ${lastFolder?.name || TEXT.untitled}`,
    }
  );

  return segments;
}
